import { useState } from 'react';

export default function ContactSection() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypotValue = (formData.get('phone_alt') as string || '').trim();
    if (honeypotValue) {
      setFormStatus('success');
      setFormMessage('感谢您的留言，我们会尽快回复！');
      return;
    }

    const name = (formData.get('name') as string).trim();
    const email = (formData.get('email') as string).trim();
    const message = (formData.get('message') as string).trim();

    if (!name || !email || !message) {
      setFormStatus('error');
      setFormMessage('请填写所有必填字段');
      return;
    }

    if (message.length > 500) {
      setFormStatus('error');
      setFormMessage('留言内容不能超过500个字符');
      return;
    }

    try {
      const body = new URLSearchParams();
      body.append('name', name);
      body.append('email', email);
      body.append('message', message);

      const response = await fetch('https://readdy.ai/api/form/d9nkbq68mbnljin5a2cg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });

      const responseText = await response.text();
      let parsed: any = {};
      try { parsed = JSON.parse(responseText); } catch { /* ignore */ }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormMessage('感谢您的留言，我们会尽快回复！');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
        if (serverMsg && serverMsg.includes('spam')) {
          setFormStatus('error');
          setFormMessage('提交失败，请稍后再试');
        } else {
          setFormStatus('error');
          setFormMessage(serverMsg || '提交失败，请稍后再试');
        }
      }
    } catch {
      setFormStatus('error');
      setFormMessage('网络错误，请稍后再试');
    }
  };

  return (
    <section className="w-full px-4 md:px-6 lg:px-8 py-12 bg-background-100">
      <div className="max-w-[900px] mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
          <div>
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-4">联系我们</h2>
            <p className="text-sm text-foreground-600 leading-relaxed mb-6">
              如果您有任何问题、建议或合作意向，欢迎通过以下方式联系我们，或填写右侧表单，我们会在24小时内回复您。
            </p>
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 flex items-center justify-center rounded-full bg-primary-100 flex-shrink-0">
                  <i className="ri-mail-line text-primary-600"></i>
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground-900">电子邮箱</p>
                  <p className="text-sm text-foreground-500">contact@51yingshi.com</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 flex items-center justify-center rounded-full bg-primary-100 flex-shrink-0">
                  <i className="ri-phone-line text-primary-600"></i>
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground-900">客服电话</p>
                  <p className="text-sm text-foreground-500">400-888-8888</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 flex items-center justify-center rounded-full bg-primary-100 flex-shrink-0">
                  <i className="ri-map-pin-line text-primary-600"></i>
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground-900">公司地址</p>
                  <p className="text-sm text-foreground-500">北京市朝阳区影视产业园区A座1501室</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 flex items-center justify-center rounded-full bg-primary-100 flex-shrink-0">
                  <i className="ri-time-line text-primary-600"></i>
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground-900">工作时间</p>
                  <p className="text-sm text-foreground-500">周一至周日 9:00 - 21:00</p>
                </div>
              </div>
            </div>
          </div>

          <div>
            <form
              onSubmit={handleSubmit}
              data-readdy-form=""
              className="bg-background-50 rounded-xl p-6 space-y-4"
            >
              <h3 className="text-base font-heading font-bold text-foreground-900 mb-2">发送消息</h3>

              <input
                type="text"
                name="phone_alt"
                className="bot-check"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
              />

              <div>
                <label htmlFor="contact-name" className="block text-xs font-medium text-foreground-700 mb-1.5">
                  姓名 <span className="text-primary-500">*</span>
                </label>
                <input
                  id="contact-name"
                  name="name"
                  type="text"
                  required
                  className="w-full px-3 py-2.5 bg-background-100 border border-background-200 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                  placeholder="请输入您的姓名"
                />
              </div>

              <div>
                <label htmlFor="contact-email" className="block text-xs font-medium text-foreground-700 mb-1.5">
                  邮箱 <span className="text-primary-500">*</span>
                </label>
                <input
                  id="contact-email"
                  name="email"
                  type="email"
                  required
                  className="w-full px-3 py-2.5 bg-background-100 border border-background-200 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                  placeholder="请输入您的邮箱"
                />
              </div>

              <div>
                <label htmlFor="contact-message" className="block text-xs font-medium text-foreground-700 mb-1.5">
                  留言内容 <span className="text-primary-500">*</span>
                </label>
                <textarea
                  id="contact-message"
                  name="message"
                  required
                  rows={4}
                  maxLength={500}
                  className="w-full px-3 py-2.5 bg-background-100 border border-background-200 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors resize-none"
                  placeholder="请输入您的留言内容（500字以内）"
                />
                <p className="text-xs text-foreground-400 mt-1">最多500个字符</p>
              </div>

              <button
                type="submit"
                className="w-full py-2.5 bg-primary-500 hover:bg-primary-600 text-white rounded-lg text-sm font-semibold transition-colors whitespace-nowrap cursor-pointer"
              >
                提交留言
              </button>

              {formStatus === 'success' && (
                <p className="text-sm text-accent-600 text-center font-medium">{formMessage}</p>
              )}
              {formStatus === 'error' && (
                <p className="text-sm text-primary-600 text-center font-medium">{formMessage}</p>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}